import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  defaultPaymentFrequency,
  paymentModels,
} from '@nxsys/constants/AppConstants';
import { useGetNetPayList } from '@nxsys/hooks/api/holidayPay';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetNetPayListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import {
  formatFullName,
  getTaxWeekTaxYear,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoneyExport } from '@nxsys/utils/number-helper';
import { ExportToCsv } from 'export-to-csv';
import { debounce, find, toNumber } from 'lodash';
import NetPayFilterModal, {
  NetPayFilterValues,
} from '../components/NetPayFilterModal';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getNetPayColumns } from './columns';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';

const NetPayReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch();
  const { payrollCountry, isBureauEmployer, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { netPayList } = useSelector((state: AppState) => state.reportStore);
  const { list, pagination, loading, queryParams } = netPayList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<INetPay[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const { loading: loadingNetPayList, run: getNetPayList } = useGetNetPayList();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.netPayReport']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetNetPayListParams) => {
      const newParams = {
        ...params,
        companyRefs: isBureauEmployer ? user?.companyUserIds : undefined,
        paymentModels: isBureau ? [PAYMENT_MODEL_ENUM.PAYEE] : undefined,
      };

      dispatch(reportActions.getNetPayList(newParams));
    },
    [dispatch, isBureauEmployer, user?.companyUserIds, isBureau],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: NetPayFilterValues) => {
    const params: GetNetPayListParams = {
      ...queryParams,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
      companyIds: values.companies?.map((c) => c.value),
      agencyIds: values.agencies?.map((a) => a.value),
      paymentModels: values.paymentModels?.map((p) => p.value),
      paymentDateFrom: values.paymentDateFrom?.format('MM/DD/YYYY'),
      paymentDateTo: values.paymentDateTo?.format('MM/DD/YYYY'),
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleExport = (data: INetPay[]) => {
    const dataExport = data.map((item) => {
      const { paid, netPay, paymentDate, payroll, timesheets, timesheetValue } =
        item;
      const {
        agencyRef,
        companyRef,
        subcontractorRef,
        taxPeriod,
        taxYear,
        currentYear,
        paymentModel,
        payFrequency,
        selfBillRef,
        net,
      } = payroll;
      return {
        subcontractorID: subcontractorRef?.id || '',
        agencyRef: `${subcontractorRef?.departmentPrefix || agencyRef?.agencyPrefix || '___'}/${
          subcontractorRef?.agencyRef || ''
        }`,
        name: formatFullName(subcontractorRef),
        taxPeriod: taxPeriod || '',
        taxYear: taxYear || currentYear || '',
        paymentModel:
          paymentModels[paymentModel as keyof typeof paymentModels] || '',
        payFrequency: payFrequency
          ? find(
              defaultPaymentFrequency,
              (item) => item?.value === payFrequency,
            )?.label
          : '',
        companyID: companyRef?.id || '',
        paymentCompany: companyRef?.companyName || '',
        ...(isBureau ? {} : { agencyName: agencyRef?.name || '' }),
        timesheetValue: formatMoneyExport(
          timesheetValue,
          showCurrencyByCountry(payrollCountry),
        ),
        netPay: formatMoneyExport(
          netPay,
          showCurrencyByCountry(payrollCountry),
        ),
        totalNet: formatMoneyExport(
          toNumber(net),
          showCurrencyByCountry(payrollCountry),
        ),
        paid: formatMoneyExport(paid, showCurrencyByCountry(payrollCountry)),
        paymentDate: paymentDate
          ? moment(paymentDate)
              .tz('Europe/London')
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '',
        selfBillID: ['CIS', 'NON_CIS'].includes(paymentModel)
          ? selfBillRef?.id || ''
          : selfBillRef?.id?.replace('SFB', 'PAY') || '',
        invoiceNo: !timesheets?.length
          ? '_'
          : Array.from(
              new Set(
                timesheets.filter((e) => e.invoiceNo).map((e) => e.invoiceNo),
              ),
            ).join(', '),
      };
    });
    const headers = [
      'Employee ID',
      isBureau ? 'Employer Ref' : 'Agency Ref',
      'Name',
      'Tax Period',
      'Tax Year',
      'Payment Model',
      'Pay Frequency',
      'Company ID',
      isBureau ? 'Employer' : 'Payment Company',
      ...(isBureau ? [] : ['Agency Name']),
      'Timesheet Values',
      'Net Pay',
      'Total Net',
      'Paid',
      'Payment Date',
      'Payslip ID',
      'Sales Inv Ref',
    ];
    const csvExporter = new ExportToCsv({
      filename: `Net-pay-export-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getNetPayList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.companyIds ||
    !!queryParams?.agencyIds ||
    !!queryParams?.paymentModels ||
    !!queryParams?.paymentDateFrom ||
    !!queryParams?.paymentDateTo;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loadingNetPayList}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              <TaxYearSelect
                initValue={currentTaxYear}
                onFilter={(value) =>
                  handleFetchData({ ...queryParams, taxYear: value.taxYear })
                }
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 4000, y: 700 }}
            columns={getNetPayColumns({
              messages,
              payrollCountry,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <NetPayFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
        isBureau={isBureau}
      />
    </AppsContainer>
  );
};

export default NetPayReport;
